import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  AppSelect,
  OutlineButton,
  SortableTh,
  StatusBadge,
  TablePagination,
  TableLoader,
} from '../../../../components/ui';
import { operationalBadge, resolveAssetOwner } from '../../../../data/master/assetOwner';
import { OWNER_TYPES, OPERATIONAL_STATUSES } from '../../../../data/master/equipmentAssets';
import { VEHICLE_CLASSES } from '../../../../data/master/equipments';
import { listEquipment } from '../../../../repositories/equipment.repository';
import { lookupEquipmentTypes } from '../../../../repositories/lookup.repository';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';

const KYC_STATUSES = ['Verified', 'Pending'];

export default function EquipmentFleetPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [assets, setAssets] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ownerTypeFilter, setOwnerTypeFilter] = useState('');
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState('');
  const [vehicleClassFilter, setVehicleClassFilter] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [operationalFilter, setOperationalFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assetRes, typeRes] = await Promise.all([
        listEquipment({ pageSize: 200 }),
        lookupEquipmentTypes(),
      ]);
      setAssets(assetRes.items || []);
      setEquipmentTypes(typeRes || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load equipment',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const equipmentTypeOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({ value: e.name, label: e.name })),
    [equipmentTypes],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets
      .map((asset) => ({ asset, owner: resolveAssetOwner(asset) }))
      .filter(({ asset, owner }) => {
        const matchQ =
          !q ||
          asset.assetId.toLowerCase().includes(q) ||
          (asset.equipmentDescription || '').toLowerCase().includes(q) ||
          asset.equipmentType.toLowerCase().includes(q) ||
          (asset.regNo || '').toLowerCase().includes(q) ||
          (asset.manufacturer || '').toLowerCase().includes(q) ||
          (asset.model || '').toLowerCase().includes(q) ||
          (asset.chassisNumber || '').toLowerCase().includes(q) ||
          owner.ownerName.toLowerCase().includes(q) ||
          owner.ownerId.toLowerCase().includes(q);
        const matchOwner = !ownerTypeFilter || asset.ownerType === ownerTypeFilter;
        const matchType =
          !equipmentTypeFilter || asset.equipmentType === equipmentTypeFilter;
        const matchClass =
          !vehicleClassFilter || asset.vehicleClass === vehicleClassFilter;
        const matchKyc = !kycFilter || (asset.status || 'Pending') === kycFilter;
        const matchOp =
          !operationalFilter ||
          (asset.operationalStatus || 'Available') === operationalFilter;
        return matchQ && matchOwner && matchType && matchClass && matchKyc && matchOp;
      });
  }, [
    assets,
    search,
    ownerTypeFilter,
    equipmentTypeFilter,
    vehicleClassFilter,
    kycFilter,
    operationalFilter,
  ]);

  const { sorted, sort, toggleSort } = useTableSort(rows, {
    assetId: (r) => r.asset.assetId,
    description: (r) => r.asset.equipmentDescription || r.asset.equipmentType,
    type: (r) => r.asset.equipmentType,
    makeModel: (r) => [r.asset.manufacturer, r.asset.model].filter(Boolean).join(' '),
    regNo: (r) => r.asset.regNo,
    owner: (r) => r.owner.ownerName,
    village: (r) => r.owner.villageName,
    kyc: (r) => r.asset.status || 'Pending',
    operational: (r) => r.asset.operationalStatus || 'Available',
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const clearFilters = () => {
    setSearch('');
    setOwnerTypeFilter('');
    setEquipmentTypeFilter('');
    setVehicleClassFilter('');
    setKycFilter('');
    setOperationalFilter('');
  };

  const refresh = () => load();

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Heading size={{ base: 'lg', xl: 'xl' }}>Equipment fleet</Heading>
          <Text color="text.secondary" mt={2} maxW="720px" fontSize={{ base: '14px', xl: '15px' }}>
            All registered equipment — villager and vendor owned. Click a row for full details,
            compliance, and deploy history.
          </Text>
        </Box>
        <OutlineButton size="sm" onClick={refresh}>
          Refresh
        </OutlineButton>
      </Flex>

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 7 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Asset ID / reg / make / owner"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Owner type"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={ownerTypeFilter}
            onChange={(v) => setOwnerTypeFilter(v ?? '')}
            options={[
              { value: '', label: 'All owners' },
              ...OWNER_TYPES.map((t) => ({ value: t, label: t })),
            ]}
          />
          <AppSelect
            label="Equipment type"
            labelFontSize="13px"
            h="40px"
            placeholder="All types"
            value={equipmentTypeFilter}
            onChange={(v) => setEquipmentTypeFilter(v ?? '')}
            options={[{ value: '', label: 'All types' }, ...equipmentTypeOptions]}
            isSearchable
            searchPlaceholder="Search type…"
          />
          <AppSelect
            label="Vehicle class"
            labelFontSize="13px"
            h="40px"
            placeholder="All classes"
            value={vehicleClassFilter}
            onChange={(v) => setVehicleClassFilter(v ?? '')}
            options={[
              { value: '', label: 'All classes' },
              ...VEHICLE_CLASSES.map((c) => ({ value: c, label: c })),
            ]}
          />
          <AppSelect
            label="KYC"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={kycFilter}
            onChange={(v) => setKycFilter(v ?? '')}
            options={[
              { value: '', label: 'All' },
              ...KYC_STATUSES.map((s) => ({ value: s, label: s })),
            ]}
          />
          <AppSelect
            label="Status"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={operationalFilter}
            onChange={(v) => setOperationalFilter(v ?? '')}
            options={[
              { value: '', label: 'All' },
              ...OPERATIONAL_STATUSES.map((s) => ({ value: s, label: s })),
            ]}
          />
          <OutlineButton size="sm" w="100%" h="40px" minH="40px" onClick={clearFilters}>
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        {loading ? (
          <TableLoader rows={7} columns={5} />
        ) : (
        <>
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary">
            {totalCount} equipment{totalCount === 1 ? '' : 's'}
          </Text>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="assetId" sort={sort} onSort={toggleSort}>
                  Asset ID
                </SortableTh>
                <SortableTh columnKey="description" sort={sort} onSort={toggleSort}>
                  Description
                </SortableTh>
                <SortableTh columnKey="type" sort={sort} onSort={toggleSort}>
                  Type
                </SortableTh>
                <SortableTh columnKey="makeModel" sort={sort} onSort={toggleSort}>
                  Make / Model
                </SortableTh>
                <SortableTh columnKey="regNo" sort={sort} onSort={toggleSort}>
                  Reg. No.
                </SortableTh>
                <SortableTh columnKey="owner" sort={sort} onSort={toggleSort}>
                  Owner
                </SortableTh>
                <SortableTh columnKey="village" sort={sort} onSort={toggleSort}>
                  Village
                </SortableTh>
                <SortableTh columnKey="kyc" sort={sort} onSort={toggleSort}>
                  KYC
                </SortableTh>
                <SortableTh columnKey="operational" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
              </Tr>
            </Thead>
            <Tbody>
              {pageItems.map(({ asset, owner }) => (
                <Tr
                  key={asset.assetId}
                  cursor="pointer"
                  _hover={{ bg: 'bg.input' }}
                  onClick={() => navigate(`/admin/equipments/fleet/${asset.assetId}`)}
                >
                  <Td fontFamily="mono" fontSize="13px">
                    {asset.assetId}
                  </Td>
                  <Td fontWeight="600">{asset.equipmentDescription || asset.equipmentType}</Td>
                  <Td fontSize="13px" color="text.secondary">
                    {asset.equipmentType}
                  </Td>
                  <Td fontSize="14px">
                    {[asset.manufacturer, asset.model].filter(Boolean).join(' ')}
                    {asset.capacity ? (
                      <Text fontSize="12px" color="text.secondary">
                        {asset.capacity} {asset.capacityUom}
                      </Text>
                    ) : null}
                  </Td>
                  <Td fontFamily="mono" fontSize="13px">
                    {asset.regNo}
                  </Td>
                  <Td>
                    <StatusBadge status={owner.ownerType === 'Vendor' ? 'info' : 'pending'}>
                      {owner.ownerType}
                    </StatusBadge>
                    <Text fontWeight="600" fontSize="13px" mt={1}>
                      {owner.ownerName}
                    </Text>
                    <Text fontSize="12px" color="text.secondary" fontFamily="mono">
                      {owner.ownerId}
                    </Text>
                  </Td>
                  <Td fontSize="13px">{owner.villageName || '—'}</Td>
                  <Td>
                    <StatusBadge
                      status={
                        asset.status === 'Verified'
                          ? 'verified'
                          : asset.status === 'Rejected'
                            ? 'error'
                            : 'pending'
                      }
                    >
                      {asset.status || 'Pending'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <StatusBadge status={operationalBadge(asset.operationalStatus)}>
                      {asset.operationalStatus || 'Available'}
                    </StatusBadge>
                  </Td>
                </Tr>
              ))}
              {pageItems.length === 0 ? (
                <Tr>
                  <Td colSpan={9}>
                    <Text color="text.secondary" py={10} textAlign="center">
                      No equipment matches your filters.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
        )}
      </AppCard>
    </Box>
  );
}
